import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PERIOD_LABELS = {
  weekly: 'Uker',
  monthly: 'Måneder',
  quarterly: 'Kvartaler',
  halfyearly: 'Halvår',
  yearly: 'År',
  all: 'Alle år',
};

/**
 * Public embed page for anonymized training participation statistics.
 *
 * URL: /embed/stats/:orgId?period=monthly&theme=light&title=1
 *  - period: weekly | monthly | quarterly | halfyearly | yearly | all
 *  - theme: dark (default) | light
 *  - title: 1 to show heading, 0 to hide
 *
 * Returns only anonymized aggregate counts. No personal data exposed.
 */
const StatsEmbed = () => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const period = searchParams.get('period') || 'monthly';
  const theme = searchParams.get('theme') || 'dark';
  const showTitle = searchParams.get('title') !== '0';

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/api/public/stats/${orgId}?period=${period}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'Kunne ikke laste statistikk');
      });
    return () => { cancelled = true; };
  }, [orgId, period]);

  const isLight = theme === 'light';
  const colors = useMemo(() => ({
    bg: isLight ? '#ffffff' : '#1f2937',
    card: isLight ? '#f9fafb' : '#111827',
    text: isLight ? '#111827' : '#f3f4f6',
    muted: isLight ? '#6b7280' : '#9ca3af',
    bar: '#facc15',
    barTop: '#fde047',
    grid: isLight ? '#e5e7eb' : '#374151',
  }), [isLight]);

  if (error) {
    return (
      <div style={{ padding: 24, color: colors.text, background: colors.bg, fontFamily: 'system-ui, sans-serif' }}>
        Feil: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 24, color: colors.muted, background: colors.bg, fontFamily: 'system-ui, sans-serif' }}>
        Laster statistikk…
      </div>
    );
  }

  const maxVal = Math.max(...data.values, 1);

  return (
    <div
      style={{
        background: colors.bg,
        color: colors.text,
        padding: 20,
        fontFamily: 'system-ui, sans-serif',
        minHeight: '100vh',
      }}
      data-testid="stats-embed"
    >
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            {data.organization_name}
          </h2>
          <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: 13 }}>
            Anonymisert oppmøtestatistikk · {PERIOD_LABELS[period] || period}
          </p>
        </div>
      )}

      {/* Total */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ background: colors.card, padding: '12px 16px', borderRadius: 8, border: `1px solid ${colors.grid}` }}>
          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Totale oppmøter</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.bar }}>{data.total}</div>
        </div>
        <div style={{ background: colors.card, padding: '12px 16px', borderRadius: 8, border: `1px solid ${colors.grid}` }}>
          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Antall {PERIOD_LABELS[period]?.toLowerCase() || 'perioder'}</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{data.values.length}</div>
        </div>
      </div>

      {/* Bar chart (CSS only) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          minHeight: 220,
          paddingTop: 20,
          paddingBottom: 8,
          borderBottom: `1px solid ${colors.grid}`,
          overflowX: 'auto',
        }}
      >
        {data.values.length === 0 && (
          <div style={{ color: colors.muted, alignSelf: 'center', padding: 20 }}>
            Ingen data for denne perioden.
          </div>
        )}
        {data.values.map((v, i) => {
          const h = Math.round((v / maxVal) * 200);
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36, flex: '0 0 auto' }}>
              <div style={{ fontSize: 11, color: colors.muted, marginBottom: 4 }}>{v}</div>
              <div
                style={{
                  width: 28,
                  height: h,
                  background: `linear-gradient(180deg, ${colors.barTop}, ${colors.bar})`,
                  borderRadius: '4px 4px 0 0',
                  transition: 'height 0.3s ease',
                }}
                title={`${data.labels[i]}: ${v} oppmøter`}
              />
              <div style={{ fontSize: 10, color: colors.muted, marginTop: 6, transform: 'rotate(-45deg)', whiteSpace: 'nowrap', transformOrigin: 'top left' }}>
                {data.labels[i]}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, fontSize: 10, color: colors.muted, textAlign: 'right' }}>
        Drevet av Aktivlogg · Oppdatert {new Date(data.generated_at).toLocaleString('no-NO')}
      </div>
    </div>
  );
};

export default StatsEmbed;
